import { formaterDevise } from '../../../../partage/format/montant';
import { enMinusculeInitiale } from '../../../../partage/format/texte';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { environment } from '../../../../../environments/environment';

import { Formule } from '../formules.interface';
import {
  AjoutCompteBancaire,
  CompteBancaire,
  ComptesBancairesListe,
  DialogAcompteRedditionData,
  DialogDataInput,
  EtatDernierImportCommandesWeb,
  PreviewAcompte,
  PreviewReddition,
  TypeDialog,
} from './dialog-acompte-reddition.interface';

@Injectable()
export class CompteBancaireService {
  private _httpClient = inject(HttpClient);

  dialogData = signal<DialogDataInput | undefined>(undefined);
  formule = computed(() => (this.dialogData() ? this.dialogData()!.formule : ({} as Formule)));
  type = computed(() => this.dialogData()?.type);

  recupererInfoAcompte$ = toObservable(this.dialogData).pipe(
    filter(dial => dial != undefined && dial.type === TypeDialog.acompte),
    map(d => d?.formule),
    distinctUntilChanged((x, y) => {
      return x?.id === y?.id;
    }),
    switchMap(x => {
      const href = `${environment.backendUrl}redditions/formules/${x?.id}/preview-acompte`;
      return this._httpClient.get<PreviewAcompte>(href);
    }),
  );

  recupererInfoAcompte = toSignal(this.recupererInfoAcompte$);
  infoAcompte = computed(() => (this.recupererInfoAcompte() ? this.recupererInfoAcompte()! : ({} as PreviewAcompte)));

  reloadComtpe = signal<boolean>(true);

  compteBancaireComputed = computed(() => ({
    formule: this.formule(),
    reload: this.reloadComtpe(),
    type: this.type(),
    infoReddition: this.recupererInfoReddition(),
    infoAcompte: this.recupererInfoAcompte(),
  }));

  private recuperationCompteBancaire = toSignal(
    toObservable(this.compteBancaireComputed).pipe(
      filter(cb => {
        if (cb.formule.nomClient === undefined || !cb.reload) return false;

        if (cb.type === TypeDialog.reddition) {
          return cb.infoReddition?.etatDernierImportCommandesWeb === EtatDernierImportCommandesWeb.Ok;
        }

        return cb.infoAcompte !== undefined;
      }),
      switchMap(cb => {
        const href = `${environment.backendUrl}redditions/client/${cb.formule.nomClient}/liste-comptes-bancaires`;
        return this._httpClient.get<ComptesBancairesListe>(href).pipe(
          map(compte => {
            this.compteBancaires.set(compte.comptesBancaires);
            this.reloadComtpe.set(false);
          }),
        );
      }),
    ),
  );

  compteBancaires = signal<CompteBancaire[]>([]);

  recupererInfoReddition$ = toObservable(this.dialogData).pipe(
    filter(dial => dial != undefined && dial.type === TypeDialog.reddition),
    map(d => d!.formule),
    distinctUntilChanged((x, y) => {
      return x?.id === y?.id;
    }),
    switchMap(x => {
      const href = `${environment.backendUrl}redditions/formules/${x.id}/preview-reddition`;
      return this._httpClient.get<PreviewReddition>(href);
    }),
  );

  recupererInfoReddition = toSignal(this.recupererInfoReddition$);
  infoReddition = computed(() =>
    this.recupererInfoReddition() ? this.recupererInfoReddition()! : ({} as PreviewReddition),
  );

  compteBancaireAjout = signal<AjoutCompteBancaire | undefined>(undefined);
  creerCompteBancaire = signal<boolean>(false);

  private ajoutCompteBancaire = toSignal(
    toObservable(this.compteBancaireAjout).pipe(
      filter(Boolean),
      switchMap(compteBancaire => {
        return this.ajouterCompteBancaire$(compteBancaire).pipe(
          map(() => {
            this.creerCompteBancaire.set(false);
            this.reloadComtpe.set(true);
          }),
        );
      }),
    ),
  );

  ajouterCompteBancaire$(compte: AjoutCompteBancaire) {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    const href = `${environment.backendUrl}redditions/clients/${compte.idClient}/ajouter-compte-bancaire`;

    return this._httpClient.post(
      href,
      { nomTitulaire: compte.nomTitulaire, iban: compte.iban, bic: compte.bic },
      httpOptions,
    );
  }

  infosDialog = computed(() => {
    const evt = this.formule();
    const type = this.type();
    if (type === TypeDialog.acompte) {
      const infoAcompte = this.recupererInfoAcompte();
      if (infoAcompte) {
        /* PAS de repli sur `totalReverse` ici. Un `?? 0` rendrait le plafond egal au
           reversement ENTIER : l'operateur pourrait creer un acompte du montant total
           en ignorant ceux deja verses. Sans repli, un champ absent donne `NaN`, et
           `montant <= NaN` etant faux, le formulaire reste invalide — la panne ferme.
           Le champ est de toute facon declare requis (`totalReverse: number`) et
           dereference sans garde dans la liste principale : le cas ne se produit pas. */
        const montantMaxAcompte = Math.max(
          0,
          parseFloat((infoAcompte.montantReverseAuClient - evt.totalReverse).toFixed(2)),
        );
        const data: DialogAcompteRedditionData = {
          btnValide: `Créer l'acompte`,
          btnAnnuler: 'Annuler',
          actionEstBLoquee: false,
          type: 'acompte',
          idClient: evt.nomClient,
          MontantMaxAcompte: montantMaxAcompte,
          message: `Vous êtes sur le point de générer un acompte sur la formule
          <br><strong>${evt.nom}</strong><br>
          Numéro de session <strong>${evt.idAparte}</strong>.<br>
          Montant global du reversement à effectuer : <strong>${formaterDevise(infoAcompte.montantReverseAuClient)}</strong><br>
          Montant global de la commission Tick&Live TTC : ${formaterDevise(infoAcompte.montantTotalCommissionsTTC)}<br>
           ${evt.totalReverse > 0 ? `Acompte déjà versé : ${formaterDevise(evt.totalReverse)}<br>` : ''}
          Montant maximum de l'acompte : <strong>${formaterDevise(montantMaxAcompte)}</strong><br>
          <strong>${infoAcompte.estSpecifique ? `La règle est spécifique pour la formule` : ''}</strong>
          <br>
          Pour chaque formule vendue, ${enMinusculeInitiale(infoAcompte.libelleRegleCalculCommissionsFormule)}
          <br>`,
        };
        return data;
      }
    } else if (type === TypeDialog.reddition) {
      const infoReddition = this.recupererInfoReddition();
      if (infoReddition) {
        const restantDu = Math.max(0, parseFloat((infoReddition.montantReverseAuClient - evt.totalReverse).toFixed(2)));
        const data: DialogAcompteRedditionData = {
          btnValide: `Lancer la reddition`,
          btnAnnuler: 'Annuler',
          actionEstBLoquee: infoReddition.etatDernierImportCommandesWeb !== EtatDernierImportCommandesWeb.Ok,
          infoBlocageMessage: getBlocageMessage(infoReddition.etatDernierImportCommandesWeb),
          type: 'reddition',
          idClient: evt.nomClient,
          MontantMaxAcompte: 0,
          message: `Vous êtes sur le point de générer une reddition sur la formule
          <br><strong>${evt.nom}</strong><br> Numéro de session <strong>${evt.idAparte}</strong>.<br>
        Montant global du reversement à effectuer : <strong>${formaterDevise(infoReddition.montantReverseAuClient)}</strong><br>
        Montant global de la commission Tick&Live TTC : ${formaterDevise(infoReddition.montantTotalCommissionsTTC)}<br>
         ${evt.totalReverse > 0 ? `Acompte déjà versé : ${formaterDevise(evt.totalReverse)}<br>` : ''}
         Restant dû : <strong>${formaterDevise(restantDu)}</strong><br>
        <strong>${infoReddition.estSpecifique ? `La règle est spécifique pour la formule` : ''}</strong>
          <br>
        Pour chaque formule vendue, ${enMinusculeInitiale(infoReddition.libelleRegleCalculCommissionsFormule)}
        <br>`,
        };
        return data;
      }
    }

    return {} as DialogAcompteRedditionData;
  });
}

function getBlocageMessage(etatDernierImportCommandesWeb: EtatDernierImportCommandesWeb): string | undefined {
  if (etatDernierImportCommandesWeb === EtatDernierImportCommandesWeb.Ok) {
    return undefined;
  } else if (etatDernierImportCommandesWeb === EtatDernierImportCommandesWeb.EnCours) {
    return "Impossible de faire la reddition car l'import des dernières commandes est en cours.";
  } else {
    return "Impossible de faire la reddition car l'import des dernières commandes a échoué. Veuillez contacter le service IT.";
  }
}
